<template>
  <TransitionRoot appear :show="props.show" as="template">
    <Dialog as="div" class="relative z-50" @close="emit('close')">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="ResumeStatistiqueModal__panel w-full max-w-2xl transform rounded-lg bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]"
            >
              <div
                class="ResumeStatistiqueModal__header flex items-center justify-between px-6 py-4 border-b border-gray-200"
              >
                <h2 class="text-lg font-semibold text-gray-900">
                  {{ $t('resumeStatistique.title', 'Résumé statistique communal') }}
                </h2>
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                  :aria-label="$t('resumeStatistique.closeAria', 'Fermer')"
                  @click="emit('close')"
                >
                  <Icon icon="mdi:close" class="w-6 h-6" />
                </button>
              </div>
              <div class="ResumeStatistiqueModal__body p-6 overflow-y-auto">
                <p
                  v-if="!hasCommune"
                  class="ResumeStatistiqueModal__notice text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4"
                >
                  {{ $t('resumeStatistique.selectCommune', 'Veuillez sélectionner une commune.') }}
                  {{
                    $t(
                      'resumeStatistique.illustrativeData',
                      'Données illustratives affichées ci-dessous.',
                    )
                  }}
                </p>
                <p v-if="resume?.intro" class="ResumeStatistiqueModal__intro text-gray-700 mb-6">
                  {{ resume.intro }}
                </p>
                <template v-for="(domaine, dIdx) in resume?.domaines" :key="dIdx">
                  <section class="ResumeStatistiqueModal__domaineSection mt-8 first:mt-0">
                    <h3
                      class="ResumeStatistiqueModal__domaineTitle text-base font-semibold text-gray-900 mt-6 mb-2"
                    >
                      {{ dIdx + 1 }}. {{ domaine.titre }}
                    </h3>
                    <p
                      v-if="domaine.introDetail"
                      class="ResumeStatistiqueModal__domaineIntro text-sm text-gray-600 mb-4"
                    >
                      {{ domaine.introDetail }}
                    </p>
                    <template v-for="(sousDomaine, sIdx) in domaine.sousDomaines" :key="sIdx">
                      <h4
                        class="ResumeStatistiqueModal__sousDomaineTitle text-sm font-medium text-gray-800 mt-4 mb-2"
                      >
                        {{ sousDomaine.titre }}
                      </h4>
                      <template v-for="(ind, iIdx) in sousDomaine.indicateurs" :key="iIdx">
                        <div class="ResumeStatistiqueModal__indicateur flex flex-col gap-2 mb-4">
                          <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <span class="text-sm text-gray-600">{{ ind.libelle }} :</span>
                            <strong class="text-gray-900">{{ ind.chiffreCle }}</strong>
                            <template
                              v-if="ind.chiffreCleNational != null && ind.chiffreCleNational !== ''"
                            >
                              <span
                                class="ResumeStatistiqueModal__nationalBadge text-xs text-gray-500"
                              >
                                ({{ $t('resumeStatistique.nationalScale') }} :
                                {{ ind.chiffreCleNational }})
                              </span>
                            </template>
                          </div>
                          <p
                            v-if="ind.description"
                            class="ResumeStatistiqueModal__indicateurDesc text-sm text-gray-600 leading-relaxed"
                          >
                            {{ ind.description }}
                          </p>
                          <div
                            v-if="ind.chartData && ind.chartData.labels.length"
                            class="ResumeStatistiqueModal__chartBlock"
                          >
                            <p
                              v-if="ind.chartData.valuesNational?.length"
                              class="ResumeStatistiqueModal__chartLegend text-xs text-gray-500 mb-1"
                            >
                              {{ $t('resumeStatistique.comparisonNational') }}
                            </p>
                            <div class="ResumeStatistiqueModal__chartCtn h-44">
                              <canvas
                                :ref="(el) => setChartRef(el, dIdx, sIdx, iIdx)"
                                class="w-full h-full"
                              />
                            </div>
                          </div>
                        </div>
                      </template>
                    </template>
                  </section>
                </template>
                <div
                  v-if="isLoading"
                  class="ResumeStatistiqueModal__loader flex justify-center py-8"
                >
                  <SpinnerLoader />
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import Icon from '@/components/ui/Icon.vue'
import SpinnerLoader from '@/components/ui/SpinnerLoader.vue'
import type { GeoData } from '@/models/interfaces/geo/GeoData'
import type { ResumeCommune } from '@/models/interfaces/ResumeStatistique'
import { i18n } from '@/plugins/i18n'
import { CommuneService } from '@/services/commune/CommuneService'
import { getResumeCommune } from '@/services/resumeStatistique/ResumeStatistiqueService'
import { useMyMapStore } from '@/stores/myMapStore'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { Chart, registerables } from 'chart.js'
import { computed, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'

const mapStore = useMyMapStore()

Chart.register(...registerables)

const props = defineProps<{
  show: boolean
  commune: GeoData | null
}>()

const emit = defineEmits<{
  close: []
}>()

const resume = ref<ResumeCommune | null>(null)
const isLoading = ref(false)
const chartInstances: Chart[] = []

const hasCommune = computed(() => props.commune != null)

function setChartRef(
  el: Element | ComponentPublicInstance | null,
  dIdx: number,
  sIdx: number,
  iIdx: number,
) {
  const canvas = el && typeof el === 'object' && 'tagName' in el ? (el as HTMLCanvasElement) : null
  if (!canvas || canvas.tagName !== 'CANVAS') return
  const ind = resume.value?.domaines[dIdx]?.sousDomaines[sIdx]?.indicateurs[iIdx]
  const data = ind?.chartData
  const chartType = ind?.chartType ?? 'bar'
  if (!data?.labels.length) return
  if (chartInstances.some((c) => c.canvas === canvas)) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const communeLabel = i18n.t('resumeStatistique.commune')
  const nationalLabel = i18n.t('resumeStatistique.nationalScale')

  if (chartType === 'doughnut') {
    const lastCommune = data.values[data.values.length - 1]
    const lastNational =
      data.valuesNational?.[data.valuesNational.length - 1] ?? Math.round(lastCommune * 0.9)
    const chart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: [communeLabel, nationalLabel],
        datasets: [
          {
            data: [lastCommune, lastNational],
            backgroundColor: ['rgba(59, 130, 246, 0.8)', 'rgba(156, 163, 175, 0.6)'],
            borderColor: ['rgb(37, 99, 235)', 'rgb(107, 114, 128)'],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true },
        },
      },
    })
    chartInstances.push(chart)
    return
  }

  const datasets: {
    label: string
    data: number[]
    backgroundColor: string
    borderColor: string
    borderWidth: number
    fill?: boolean
    tension?: number
  }[] = [
    {
      label: communeLabel,
      data: data.values,
      backgroundColor: chartType === 'line' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.6)',
      borderColor: 'rgb(37, 99, 235)',
      borderWidth: 2,
      fill: chartType === 'line',
      tension: chartType === 'line' ? 0.3 : undefined,
    },
  ]
  if (data.valuesNational && data.valuesNational.length === data.labels.length) {
    datasets.push({
      label: nationalLabel,
      data: data.valuesNational,
      backgroundColor:
        chartType === 'line' ? 'rgba(156, 163, 175, 0.15)' : 'rgba(156, 163, 175, 0.5)',
      borderColor: 'rgb(107, 114, 128)',
      borderWidth: 2,
      fill: chartType === 'line',
      tension: chartType === 'line' ? 0.3 : undefined,
    })
  }
  const chart = new Chart(ctx, {
    type: chartType,
    data: {
      labels: data.labels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: { beginAtZero: true },
      },
      plugins: {
        legend: {
          display: datasets.length > 1,
        },
      },
    },
  })
  chartInstances.push(chart)
}

/**
 * Resolves the real cmnCode for the résumé statistique endpoint: prefers the commune locked in
 * the communal geoportail (mapStore.activeCommuneCode), else looks it up by name from the header
 * search result so the panel still targets a real commune outside the communal geoportail.
 */
async function resolveCmnCode(commune: GeoData | null): Promise<string | null> {
  if (mapStore.activeCommuneCode) return mapStore.activeCommuneCode
  const name = commune?.name?.trim()
  if (!name) return null
  try {
    const matches = await CommuneService.searchCommunes(name, 1)
    return matches[0]?.cmnCode ?? null
  } catch {
    return null
  }
}

watch(
  () => [props.show, props.commune] as const,
  async ([show, commune]) => {
    chartInstances.forEach((c) => c.destroy())
    chartInstances.length = 0
    if (!show) {
      resume.value = null
      return
    }
    resume.value = null
    isLoading.value = true
    try {
      const cmnCode = await resolveCmnCode(commune ?? null)
      resume.value = await getResumeCommune(commune ?? null, cmnCode)
    } finally {
      isLoading.value = false
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.ResumeStatistiqueModal__panel {
  min-height: 12rem;
}

.ResumeStatistiqueModal__header {
  flex-shrink: 0;
}

.ResumeStatistiqueModal__body {
  flex: 1 1 auto;
}

.ResumeStatistiqueModal__notice {
  line-height: 1.4;
}

.ResumeStatistiqueModal__intro {
  font-size: 0.9375rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.ResumeStatistiqueModal__domaineSection {
  margin-top: 2rem;

  &:first-child {
    margin-top: 0;
  }
}

.ResumeStatistiqueModal__domaineTitle {
  font-size: 1rem;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.ResumeStatistiqueModal__domaineIntro {
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  color: rgb(75 85 99);
}

.ResumeStatistiqueModal__sousDomaineTitle {
  font-size: 0.875rem;
}

.ResumeStatistiqueModal__nationalBadge {
  font-weight: 400;
}

.ResumeStatistiqueModal__indicateurDesc {
  margin-top: 0.25rem;
  margin-bottom: 0;
}

.ResumeStatistiqueModal__chartBlock {
  margin-top: 0.5rem;
}

.ResumeStatistiqueModal__chartLegend {
  font-weight: 500;
}

.ResumeStatistiqueModal__chartCtn {
  min-height: 11rem;
  max-width: 22rem;
}
</style>
